import { Box, Button, Group } from '@mantine/core';
import { useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getSafeMarkdownHref, isExternalMarkdownHref } from '@/utils/markdown.ts';

type StepExecutionLogMessageProps = {
  message: string;
};

const COMPACT_LOG_CHARACTER_LIMIT = 700;
const COMPACT_LOG_LINE_LIMIT = 12;

const shouldCompactLog = (message: string) =>
  message.length > COMPACT_LOG_CHARACTER_LIMIT || message.split(/\r?\n/).length > COMPACT_LOG_LINE_LIMIT;

const getCompactPreview = (message: string) => {
  const lines = message.split(/\r?\n/);
  const preview = lines.slice(0, COMPACT_LOG_LINE_LIMIT).join('\n').slice(0, COMPACT_LOG_CHARACTER_LIMIT);
  return `${preview.trimEnd()}…`;
};

const logRemarkPlugins = [remarkGfm];

const logMarkdownComponents: Components = {
  a: ({ href, children, node: _node, ...props }) => {
    const safeHref = getSafeMarkdownHref(href);
    if (!safeHref) {
      return <>{children}</>;
    }
    const isExternal = isExternalMarkdownHref(safeHref);

    return (
      <a
        {...props}
        href={safeHref}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    );
  },
  p: ({ children, ...props }) => (
    <p
      {...props}
      style={{
        margin: 0,
        whiteSpace: 'pre-wrap',
      }}
    >
      {children}
    </p>
  ),
  ul: ({ children, ...props }) => (
    <ul
      {...props}
      style={{
        margin: '4px 0 0',
        paddingLeft: '18px',
      }}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      {...props}
      style={{
        margin: '4px 0 0',
        paddingLeft: '18px',
      }}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li
      {...props}
      style={{
        margin: 0,
        whiteSpace: 'pre-wrap',
      }}
    >
      {children}
    </li>
  ),
  pre: ({ children, ...props }) => (
    <pre
      {...props}
      style={{
        overflowX: 'auto',
        backgroundColor: '#F8FAFC',
        border: '1px solid #E5E7EB',
        borderRadius: '4px',
        padding: '10px 12px',
        fontSize: '13px',
        lineHeight: 1.45,
        margin: '4px 0 0',
      }}
    >
      {children}
    </pre>
  ),
  code: ({ children, className, ...props }) => (
    <code
      {...props}
      className={className}
      style={{
        backgroundColor: className ? undefined : '#F1F5F9',
        borderRadius: className ? undefined : '3px',
        padding: className ? undefined : '1px 4px',
        fontSize: '0.95em',
      }}
    >
      {children}
    </code>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      {...props}
      style={{
        borderLeft: '3px solid #CBD5E1',
        color: '#475569',
        margin: '4px 0 0',
        padding: '2px 0 2px 12px',
      }}
    >
      {children}
    </blockquote>
  ),
  img: () => null,
  input: ({ ...props }) => <input {...props} disabled readOnly style={{ marginRight: '6px' }} />,
};

const StepExecutionLogMessage = ({ message }: StepExecutionLogMessageProps) => {
  const [expanded, setExpanded] = useState(false);
  const isCompact = shouldCompactLog(message);
  const visibleMessage = isCompact && !expanded ? getCompactPreview(message) : message;

  const copyFullMessage = () => {
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(message).catch(() => undefined);
    }
  };

  return (
    <Box style={{ minWidth: 0, maxWidth: '100%' }}>
      <Box
        style={{
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          fontSize: '14px',
          lineHeight: 1.4,
        }}
      >
        <ReactMarkdown remarkPlugins={logRemarkPlugins} components={logMarkdownComponents}>
          {visibleMessage}
        </ReactMarkdown>
      </Box>
      {isCompact && (
        <Group gap="xs" mt={4}>
          <Button color="gray" variant="subtle" size="compact-xs" onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Collapse log' : 'Expand log'}
          </Button>
          <Button color="gray" variant="subtle" size="compact-xs" onClick={copyFullMessage}>
            Copy full log
          </Button>
        </Group>
      )}
    </Box>
  );
};

export default StepExecutionLogMessage;
